import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { generateAISuggestions } from '../../services/aiConnectionEngine';
import { writeFetch, getSession } from '../../api';

const {
  FiZap, FiX, FiCheck, FiChevronDown, FiChevronUp,
  FiRefreshCw, FiUsers, FiLink, FiAlertTriangle,
  FiInfo, FiStar, FiEye, FiEyeOff,
} = FiIcons;

const SUGGESTIONS_TABLE = 'ai_connection_suggestions_1749200000';
const FAMILY_TABLE      = 'user_family_members_1749000000';

const CONFIDENCE_STYLES = {
  high:   { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'High Match' },
  medium: { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-400',  label: 'Likely Match' },
  low:    { bg: 'bg-gray-50',    text: 'text-gray-600',    border: 'border-gray-200',    dot: 'bg-gray-400',   label: 'Possible Match' },
};

const TYPE_ICONS = {
  tree_match:   FiLink,
  member_pair:  FiUsers,
  memorial_link: FiStar,
};

// ── Single Suggestion Card ────────────────────────────────────────────────────
function SuggestionCard({ suggestion, onAccept, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = CONFIDENCE_STYLES[suggestion.confidenceLabel] || CONFIDENCE_STYLES.low;
  const Icon = TYPE_ICONS[suggestion.type] || FiZap;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      className={`rounded-2xl border-2 ${cfg.border} ${cfg.bg} overflow-hidden`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.text} bg-white shadow-sm border ${cfg.border}`}>
            <SafeIcon icon={Icon} className="w-4 h-4" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <p className={`text-sm font-black ${cfg.text} truncate`}>{suggestion.suggestedName}</p>
              <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-white border ${cfg.border} ${cfg.text}`}>
                {cfg.label}
              </span>
            </div>
            {suggestion.suggestedRelationship && (
              <p className="text-[10px] text-text-muted font-medium mb-1">{suggestion.suggestedRelationship}</p>
            )}
            <p className="text-[11px] text-text-muted leading-relaxed line-clamp-2">{suggestion.reason}</p>

            {/* Confidence bar */}
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden border border-gray-100">
                <motion.div
                  className={`h-full ${cfg.dot} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: `${suggestion.confidenceScore}%` }}
                  transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
                />
              </div>
              <span className={`text-[9px] font-black ${cfg.text}`}>{suggestion.confidenceScore}%</span>
            </div>
          </div>

          {/* Expand toggle */}
          <button onClick={() => setExpanded(v => !v)}
            className="p-1.5 hover:bg-white rounded-lg transition-colors flex-shrink-0">
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className={`w-3.5 h-3.5 ${cfg.text}`} />
          </button>
        </div>

        {/* Signals (expanded) */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t border-white/60">
                <p className={`text-[9px] font-black uppercase tracking-widest ${cfg.text} mb-2`}>
                  AI Detection Signals
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(suggestion.signals || []).map((s, i) => (
                    <span key={i} className={`text-[9px] font-bold px-2.5 py-1 rounded-full bg-white border ${cfg.border} ${cfg.text}`}>
                      {s}
                    </span>
                  ))}
                </div>
                {suggestion.memorialName && (
                  <p className={`mt-2 text-[10px] font-bold ${cfg.text} flex items-center gap-1.5`}>
                    <SafeIcon icon={FiLink} className="w-3 h-3" />
                    Memorial: {suggestion.memorialName}
                  </p>
                )}
                {suggestion.sourceMemberName && (
                  <p className={`mt-1 text-[10px] font-medium ${cfg.text} flex items-center gap-1.5`}>
                    <SafeIcon icon={FiUsers} className="w-3 h-3" />
                    Related to: {suggestion.sourceMemberName}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => onAccept(suggestion)}
            className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 bg-white border-2 ${cfg.border} ${cfg.text} hover:shadow-sm transition-all`}
          >
            <SafeIcon icon={FiCheck} className="w-3 h-3" /> Add to Network
          </button>
          <button
            onClick={() => onDismiss(suggestion)}
            className="px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-white border-2 border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-400 transition-all"
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function AISuggestionsPanel({ userMembers = [] }) {
  const [suggestions, setSuggestions]   = useState([]);
  const [dismissed,   setDismissed]     = useState(new Set());
  const [accepted,    setAccepted]      = useState(new Set());
  const [analyzing,   setAnalyzing]     = useState(false);
  const [collapsed,   setCollapsed]     = useState(false);
  const [addingId,    setAddingId]      = useState(null);
  const [toast,       setToast]         = useState('');
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => { if (isMounted.current) setToast(''); }, 3500);
  };

  const analyse = useCallback(() => {
    if (userMembers.length === 0) return;
    setAnalyzing(true);
    // Simulate async processing with a short delay for UX
    setTimeout(() => {
      if (!isMounted.current) return;
      const results = generateAISuggestions(userMembers);
      setSuggestions(results);
      setAnalyzing(false);
    }, 800);
  }, [userMembers]);

  useEffect(() => { analyse(); }, [analyse]);

  const handleDismiss = useCallback((suggestion) => {
    setDismissed(prev => new Set([...prev, suggestion.id]));
  }, []);

  const handleAccept = useCallback(async (suggestion) => {
    const user = getSession();
    if (!user || addingId) return;
    setAddingId(suggestion.id);

    try {
      // If it's a tree_match, add the tree node as a family member
      if (suggestion.type === 'tree_match' && suggestion.nodeData) {
        const node = suggestion.nodeData;
        await writeFetch(`create/${FAMILY_TABLE}`, {
          method: 'POST',
          body: JSON.stringify({
            id: crypto.randomUUID(),
            owner_id: user.id,
            full_name: node.name,
            relationship: node.relation || suggestion.suggestedRelationship || 'Other',
            birth_year: node.birthYear || null,
            is_deceased: node.isDeceased ? 1 : 0,
            death_year: node.deathYear || null,
            bio: node.bio || '',
            avatar_url: node.avatar || '',
            linked_memorial_id: suggestion.memorialId || '',
            linked_memorial_name: suggestion.memorialName || '',
          }),
        });
        showToast(`${node.name} added to your family network from the ${suggestion.memorialName} tree`);
      } else {
        showToast(`Connection noted for ${suggestion.suggestedName}. Review and add manually if needed.`);
      }

      // Persist acceptance to DB
      await writeFetch(`create/${SUGGESTIONS_TABLE}`, {
        method: 'POST',
        body: JSON.stringify({
          id: suggestion.id,
          owner_id: user.id,
          suggestion_type: suggestion.type,
          source_member_id: suggestion.sourceMemberId || '',
          source_member_name: suggestion.sourceMemberName || '',
          suggested_name: suggestion.suggestedName,
          suggested_relationship: suggestion.suggestedRelationship || '',
          confidence_score: suggestion.confidenceScore,
          reason: suggestion.reason,
          signals: JSON.stringify(suggestion.signals || []),
          memorial_id: suggestion.memorialId || '',
          memorial_name: suggestion.memorialName || '',
          status: 'accepted',
          is_accepted: 1,
          is_dismissed: 0,
        }),
      }).catch(() => {});

      if (isMounted.current) {
        setAccepted(prev => new Set([...prev, suggestion.id]));
      }
    } catch (e) {
      showToast('Could not add member. Please try again.');
    } finally {
      if (isMounted.current) setAddingId(null);
    }
  }, [addingId]);

  const visible = suggestions.filter(s => !dismissed.has(s.id) && !accepted.has(s.id));
  const highCount = visible.filter(s => s.confidenceLabel === 'high').length;

  if (userMembers.length < 1) return null;

  return (
    <div className="bg-white rounded-[32px] border border-brand-dark shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-brand-dark">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
              </div>
              {highCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full text-white text-[9px] font-black flex items-center justify-center border-2 border-white">
                  {highCount}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-base font-serif text-accent-navy">AI Connection Suggestions</h3>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                {analyzing ? 'Analysing your network…' : `${visible.length} potential connection${visible.length !== 1 ? 's' : ''} found`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={analyse} disabled={analyzing}
              className="p-2 hover:bg-brand-light rounded-xl transition-colors text-text-muted hover:text-accent-navy">
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${analyzing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setCollapsed(v => !v)}
              className="p-2 hover:bg-brand-light rounded-xl transition-colors text-text-muted">
              <SafeIcon icon={collapsed ? FiEye : FiEyeOff} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Info banner */}
        {!collapsed && !analyzing && visible.length > 0 && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-violet-50 rounded-xl border border-violet-100">
            <SafeIcon icon={FiInfo} className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-violet-800 leading-relaxed">
              <strong>How it works:</strong> Our AI analyses shared surnames, birth year overlap, location mentions, 
              bio keywords, and memorial connections to surface likely family links.
            </p>
          </div>
        )}
      </div>

      {/* Suggestions list */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3">
              {analyzing ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 bg-brand-light rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : visible.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-12 h-12 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <SafeIcon icon={FiZap} className="w-6 h-6 text-violet-300" />
                  </div>
                  <p className="text-sm font-bold text-accent-navy mb-1">No new suggestions</p>
                  <p className="text-xs text-text-muted">
                    Add more family members with bios and birth years to unlock better AI matching.
                  </p>
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {visible.map(s => (
                    <SuggestionCard
                      key={s.id}
                      suggestion={s}
                      onAccept={handleAccept}
                      onDismiss={handleDismiss}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-4 bg-violet-700 text-white rounded-2xl shadow-xl max-w-sm"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
          >
            <SafeIcon icon={FiZap} className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}